import * as React from "react";
import { Box, Button, Grid, Slide } from "@mui/material";
import OSCRTransactionDetails from "./OSCRTransactionDetails";
import OSCRSecondaryDetails from "./OSCRSecondaryDetails";
import OSCRViewBranches from "./OSCRViewBranches";
import { OscrLevelTwoRecord, Query } from "../../interfaces";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import ResultPagination from "../ResultPagination";
import usePagination from "../../hooks/usePagination";
import LoadingIndicator from "../LoadingIndicator";
import ApiDataDownload, { apiDownloadProps } from "../ApiDataDownload";
import { fileNameFormat } from "../../common/services/Utilities";

interface Props {
  dataLoaded: boolean;
  timeout: number;
  showViewBranches: boolean;
  eventType: string;
  data: OscrLevelTwoRecord;
  viewBranchesData: OscrLevelTwoRecord[];
  setViewBranchesData: (viwBranchesData: any) => void;
  appState: Query;
}

export default function StructureChangeModalDialog({
  dataLoaded,
  timeout,
  showViewBranches,
  eventType,
  data,
  viewBranchesData,
  setViewBranchesData,
  appState,
}: Props) {
  const {
    page,
    rowsPerPage,
    handlePageJump,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePagination(1, 25);

  const [openBranch, setOpenBranch] = React.useState(false);

  const [paginationLoad, setPaginationLoad] = React.useState(false);

  React.useEffect(() => {
    if (openBranch) {
      let isMounted = true;
      setPaginationLoad(true);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}${data.ACQ_BRANCHES_HREF}&offset=${
            (page - 1) * rowsPerPage
          }&limit=${rowsPerPage}`,
        )
          .then((response) => {
            if (isMounted) {
              setViewBranchesData({
                resultData: response.data,
                dataLoaded: true,
                timeout: 300,
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            setPaginationLoad(false);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [
    data.ACQDATE,
    data.ACQ_BRANCHES,
    data.ACQ_BRANCHES_HREF,
    data.EFF,
    openBranch,
    page,
    rowsPerPage,
    setViewBranchesData,
  ]);

  const handleCloseViewBranches = () => {
    setViewBranchesData({ resultData: [], dataLoaded: false, timeout: 300 });
    setOpenBranch(false);
  };

  const handleOpenViewBranches = () => {
    setOpenBranch(true);
  };

  const ViewBranchesButton = (
    <Button
      id={"view-branches-button"}
      sx={{
        "height": 40,
        "width": 160,
        "&.MuiButton-root": {
          margin: "4px auto 24px auto",
          textAlign: "center",
          display: "block",
        },
      }}
      variant="contained"
      color="secondary"
      disableElevation
      onClick={handleOpenViewBranches}
    >
      View Branches
    </Button>
  );

  const CloseBranchesButton = (
    <Box sx={{ paddingTop: 2, paddingLeft: 3 }}>
      <Button
        id={"close-branches-button"}
        sx={{
          "height": 40,
          "width": 160,
          "&.MuiButton-root": {
            margin: "10px auto",
            textAlign: "center",
            display: "block",
          },
        }}
        variant="contained"
        color="secondary"
        disableElevation
        onClick={handleCloseViewBranches}
      >
        Close Branches
      </Button>
    </Box>
  );

  const downloadData: apiDownloadProps = {
    fileName: `${fileNameFormat(eventType, data.CERT, data.ACQ_INSTNAME)}.csv`,
    apiPath: "history",
    appState: appState,
    downloadUrl: `${ApiBaseUrl()}${data.ACQ_BRANCHES_HREF}`,
  };

  return (
    <Grid container spacing={2}>
      {!dataLoaded ? (
        <Slide
          direction="right"
          in={!dataLoaded}
          mountOnEnter
          unmountOnExit
          timeout={{ enter: timeout, exit: 0 }}
        >
          <Grid size={12}>
            <OSCRTransactionDetails eventType={eventType} data={data} />
            {showViewBranches && (
              <Grid sx={{ display: "flex", justifyContent: "center" }}>
                {ViewBranchesButton}
              </Grid>
            )}
            <OSCRSecondaryDetails eventType={eventType} data={data} />
          </Grid>
        </Slide>
      ) : (
        ""
      )}
      <Slide
        direction="left"
        in={dataLoaded}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: timeout, exit: 0 }}
      >
        <Grid size={12}>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              bgcolor: "#ffffff",
              position: "sticky",
              zIndex: 10000,
              top: 0,
              paddingTop: 1,
              alignItems: "center",
              boxShadow: "0px 8px 6px 0px  rgba(0, 0, 0, 0.4)",
            }}
          >
            {CloseBranchesButton}

            <ResultPagination
              count={Number(data.ACQ_BRANCHES)}
              handleChangePage={handleChangePage as any}
              handlePickPage={handlePageJump}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              limit={rowsPerPage}
              pageNumber={page}
              hideBottomPagination
              useSmallSize={Number(data.ACQ_BRANCHES) / rowsPerPage > 5}
            >
              <>
                {paginationLoad ? (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      marginLeft: "auto",
                      marginRight: "auto",
                      zIndex: 100,
                    }}
                  >
                    <LoadingIndicator
                      text="Loading"
                      spinnerSize={60}
                      textSx={{
                        fontSize: "1.5rem",
                        width: "100%",
                        textAlign: "center",
                        height: "100%",
                      }}
                      spinnerSx={{
                        marginTop: 24,
                        width: "100%",
                        textAlign: "center",
                      }}
                    />
                  </Box>
                ) : (
                  ""
                )}
              </>
            </ResultPagination>
            <Box
              sx={{
                "paddingRight": 3,
                "#dataDownload": {
                  "height": 40,
                  "width": 160,

                  "&.MuiButton-root": {
                    margin: "10px auto",
                    textAlign: "center",
                    display: "block",
                  },
                },
              }}
            >
              <ApiDataDownload
                apiData={[downloadData]}
                app={"structureChange"}
              />
            </Box>
          </Box>
          <Box sx={{ opacity: paginationLoad ? 0.1 : 1, padding: 3 }}>
            <OSCRViewBranches resultData={viewBranchesData} />
          </Box>
        </Grid>
      </Slide>
    </Grid>
  );
}
